export const ACTIVITY_TYPES = [
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'teams_meeting', label: 'Teams Meeting' },
  { value: 'personal_meeting', label: 'Personal Meeting' },
  { value: 'email', label: 'Email' },
  { value: 'linkedin', label: 'LinkedIn' },
];

export const ACTIVITY_RESULTS = [
  { value: 'candidate_discussed', label: 'Candidate Discussed' },
  { value: 'new_requirement', label: 'New Requirement' },
  { value: 'interview_agreed', label: 'Interview Agreed' },
  { value: 'feedback_received', label: 'Feedback Received' },
  { value: 'no_current_requirement', label: 'No Current Requirement' },
  { value: 'follow_up_required', label: 'Follow Up Required' },
  { value: 'offer_discussed', label: 'Offer Discussed' },
  { value: 'other', label: 'Other' },
];

export const ACCOUNT_STATUSES = [
  { value: 'active_client', label: 'Active Client', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'prospect', label: 'Prospect', className: 'bg-blue-100 text-blue-700' },
  { value: 'inactive', label: 'Inactive', className: 'bg-gray-100 text-gray-600' },
];

export const TASK_PRIORITIES = [
  { value: 'high', label: 'High', className: 'bg-rose-100 text-rose-700' },
  { value: 'medium', label: 'Medium', className: 'bg-amber-100 text-amber-700' },
  { value: 'low', label: 'Low', className: 'bg-slate-100 text-slate-600' },
];

export const HEALTH_META = {
  green: { label: 'Healthy', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  amber: { label: 'Needs attention', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { label: 'At risk', dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50' },
};

const findLabel = (list, value) => list.find((i) => i.value === value)?.label || value || '—';
export const typeLabel = (v) => findLabel(ACTIVITY_TYPES, v);
export const resultLabel = (v) => findLabel(ACTIVITY_RESULTS, v);
export const statusMeta = (v) => ACCOUNT_STATUSES.find((s) => s.value === v) || ACCOUNT_STATUSES[1];
export const priorityMeta = (v) => TASK_PRIORITIES.find((p) => p.value === v) || TASK_PRIORITIES[1];

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const daysAgoLabel = (days) => {
  if (days === null || days === undefined) return 'Never';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

export const contactName = (c) => (c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '');

export const todayISO = () => new Date().toISOString().slice(0, 10);

// ── Phase 2 — Recruitment Jobs + Candidate Pipeline ─────────────────────────
export const JOB_STATUSES = [
  { value: 'open', label: 'Open', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'on_hold', label: 'On Hold', className: 'bg-amber-100 text-amber-700' },
  { value: 'filled', label: 'Filled', className: 'bg-blue-100 text-blue-700' },
  { value: 'lost', label: 'Lost', className: 'bg-gray-100 text-gray-600' },
];

export const EMPLOYMENT_TYPES = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
];

export const FEE_TYPES = [
  { value: 'percentage', label: '% of salary' },
  { value: 'fixed', label: 'Fixed fee' },
];

// Ordered recruitment pipeline (rejected is terminal, shown last)
export const PIPELINE_STAGES = [
  { value: 'sourced', label: 'Sourced', className: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', col: 'border-slate-300' },
  { value: 'qualified', label: 'Qualified', className: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500', col: 'border-indigo-400' },
  { value: 'presented', label: 'Presented', className: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', col: 'border-violet-400' },
  { value: 'interview', label: 'Interview', className: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', col: 'border-blue-400' },
  { value: 'final', label: 'Final', className: 'bg-cyan-100 text-cyan-700', dot: 'bg-cyan-500', col: 'border-cyan-400' },
  { value: 'placement', label: 'Placement', className: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', col: 'border-emerald-400' },
  { value: 'rejected', label: 'Rejected', className: 'bg-rose-100 text-rose-700', dot: 'bg-rose-400', col: 'border-rose-300' },
];

export const jobStatusMeta = (v) => JOB_STATUSES.find((s) => s.value === v) || JOB_STATUSES[0];
export const stageMeta = (v) => PIPELINE_STAGES.find((s) => s.value === v) || PIPELINE_STAGES[0];
export const employmentTypeLabel = (v) => EMPLOYMENT_TYPES.find((t) => t.value === v)?.label || (v || '—');

export const fmtMoney = (amount, currency = 'EUR') => {
  if (amount === null || amount === undefined || amount === '') return null;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(amount));
  } catch {
    return `${amount}`;
  }
};

export const salaryRange = (min, max, currency = 'EUR') => {
  const a = fmtMoney(min, currency);
  const b = fmtMoney(max, currency);
  if (a && b) return `${a} – ${b}`;
  return a || b || null;
};

export const feeLabel = (job) => {
  if (!job) return null;
  if (job.fee_type === 'fixed' && job.fee_amount != null) return fmtMoney(job.fee_amount, job.currency || 'EUR');
  if (job.fee_type === 'percentage' && job.fee_percentage != null) return `${job.fee_percentage}%`;
  return null;
};

export const candidateName = (c) => (c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '');

// ── Phase 3 — Placements + Revenue + Commission ──────────────────────────────
export const INVOICE_STATUSES = [
  { value: 'pending', label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  { value: 'invoiced', label: 'Invoiced', className: 'bg-blue-100 text-blue-700' },
  { value: 'paid', label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
];

export const PLACEMENT_STATUSES = [
  { value: 'active', label: 'In Guarantee', className: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'fell_out', label: 'Fell Out', className: 'bg-rose-100 text-rose-700' },
];

export const invoiceStatusMeta = (v) => INVOICE_STATUSES.find((s) => s.value === v) || INVOICE_STATUSES[0];
export const placementStatusMeta = (v) => PLACEMENT_STATUSES.find((s) => s.value === v) || PLACEMENT_STATUSES[0];

export const computePlacementMoney = ({ salary, feeType, feePercentage, feeAmount, commissionRate }) => {
  let revenue = 0;
  if (feeType === 'fixed') revenue = Number(feeAmount) || 0;
  else if (salary && feePercentage) revenue = (Number(salary) * Number(feePercentage)) / 100;
  revenue = Math.round(revenue * 100) / 100;
  const rate = commissionRate === '' || commissionRate === null || commissionRate === undefined ? 10 : Number(commissionRate);
  const commission = Math.round(revenue * rate) / 100;
  return { revenue, commission, rate };
};

// ── Phase 6 — Software Sales Pipeline (JobastiIQ platform deals) ─────────────
export const DEAL_STAGES = [
  { value: 'prospect', label: 'Prospect', prob: 10, className: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', col: 'border-slate-300', bar: 'bg-slate-400' },
  { value: 'demo', label: 'Demo', prob: 30, className: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500', col: 'border-indigo-400', bar: 'bg-indigo-500' },
  { value: 'proposal', label: 'Proposal', prob: 50, className: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', col: 'border-violet-400', bar: 'bg-violet-500' },
  { value: 'negotiation', label: 'Negotiation', prob: 75, className: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', col: 'border-amber-400', bar: 'bg-amber-500' },
  { value: 'won', label: 'Won', prob: 100, className: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', col: 'border-emerald-400', bar: 'bg-emerald-500' },
  { value: 'lost', label: 'Lost', prob: 0, className: 'bg-rose-100 text-rose-700', dot: 'bg-rose-400', col: 'border-rose-300', bar: 'bg-rose-400' },
];

export const DEAL_SOURCES = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'referral', label: 'Referral' },
  { value: 'event', label: 'Event' },
  { value: 'partner', label: 'Partner' },
];

export const DEAL_PLANS = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'custom', label: 'Custom' },
];

export const dealStageMeta = (v) => DEAL_STAGES.find((s) => s.value === v) || DEAL_STAGES[0];
export const dealSourceLabel = (v) => DEAL_SOURCES.find((s) => s.value === v)?.label || (v || '—');
export const dealPlanLabel = (v) => DEAL_PLANS.find((s) => s.value === v)?.label || null;

export const computeDealMoney = ({ mrr, setupFee }) => {
  const m = Number(mrr) || 0;
  const arr = Math.round(m * 12 * 100) / 100;
  const tcv = Math.round((arr + (Number(setupFee) || 0)) * 100) / 100;
  return { mrr: m, arr, tcv };
};
